import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

export type DeliveryNoteTemplate = {
  id: UUID;
  name: string;
  modifiedOn: string;
  modifiedById: UUID;
  createdOn: string;
  createdById: UUID;
  defaultTemplate: boolean;
  companyId: UUID;
  data: Record<string, any>;
};

type DeliveryNoteTemplatesResponse = {
  deliveryNotes: DeliveryNoteTemplate[];
};

/**
 * Fetches all delivery note templates from the API.
 *
 * @returns {Promise<DeliveryNoteTemplate[]>} Array of delivery note templates
 * @throws {Error} If the API request fails
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Template/operation/get_all_dln_templates_dln_template_get
 */
export const fetchDeliveryNoteTemplates = async () => {
  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_TEMPLATES())
    .json<DeliveryNoteTemplatesResponse>();

  return response.deliveryNotes ?? [];
};

/**
 * React Query based custom hook for getting all delivery note templates.
 *
 * @param {Object} options - Additional options for the useQuery hook
 * @returns {UseQueryResult<DeliveryNoteTemplate[]>} The result of the useQuery hook containing the templates
 */
export const useQueryDeliveryNoteTemplates = (
  options?: Omit<
    UseQueryOptions<DeliveryNoteTemplate[]>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryFn: async () => fetchDeliveryNoteTemplates(),
    queryKey: queryKeysDeliveryNote.getTemplates(),
    ...options,
  });
};
